import styled from 'styled-components';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { getPhotoSeries } from 'redux/root.selectors';
import { useSelector } from 'react-redux';
import { RingLoader } from 'components/partials';
import { useTheme, useCurrentUser } from 'hooks';
import { useParams } from 'react-router-dom';
import { makeGet, useTranslate } from 'services';
import { AjaxResponse } from 'rxjs/ajax';
import { useState, useEffect, useCallback } from 'react';
import { PhotoSeries, WindshieldRepairReplaceDecision } from 'redux/photoseries/photoseries';
import DownloadImages from './download-images.component';

const Title = styled.h2<{paddingTop?: string}>`
  font-size: 1.75rem;
  padding-top: ${(props) => props.paddingTop || '7rem'};
`;

const Body = styled.div`
  font-style: normal;
  font-size: 1.125rem;
  line-height: 200%;
  padding-top: 1rem;
`;

const { user_feedback, vehicle_scan } = TRANSLATIONS_VALUES_KEYS;

export interface RedirectToCompanyProps {
  customerName: string,
  returnUrl: string,
}

export const RedirectToCompany = (props: RedirectToCompanyProps) => {
  const { customerName, returnUrl } = props;
  const [counter, setCounter] = useState<number>(5);
  const { translate } = useTranslate();

  useEffect(() => {
    const timer = setInterval(() => {
      if (counter > 0) setCounter(counter - 1);
    }, 1000);

    if (counter === 0) {
      window.location.replace(returnUrl);
    }

    return () => clearInterval(timer);
  }, [counter, returnUrl]);

  return (
    <>
      <Title data-testid="thank-you-title">
        { translate(user_feedback.title_thankyou) }
      </Title>
      <Body data-testid="thank-you-description">
        <div className="pr-5 pl-5">
          { translate(user_feedback.title_redirect, { publicUserPhotoSeriesRecipient: customerName, unitSeconds: counter.toString() }) }
        </div>
      </Body>
    </>
  );
};

const RingLoaderWrapper = styled.div`
  padding-top: 3rem;
`;

export interface RedirectToSigningProps {
  photoSeriesId: string,
}

export const RedirectToSigning = (props: RedirectToSigningProps) => {
  const { styles } = useTheme();
  const { photoSeriesId } = props;
  const { translate } = useTranslate();

  const queryPhotoSeries = useCallback(() => {
    makeGet(`capture/${photoSeriesId}/report-signing-status`).subscribe(
      ({ response }: AjaxResponse<any>) => {
        if (response.reportSigningUrl && response.reportSigningUrl.length > 0) {
          window.location.replace(response.reportSigningUrl);
        }
      }
    );
  }, [photoSeriesId]);

  useEffect(() => {
    const timer = setInterval(() => queryPhotoSeries(), 5000);
    return () => clearInterval(timer);
  }, [queryPhotoSeries]);

  return (
    <>
      <Title data-testid="thank-you-title">
        { translate(user_feedback.title_thankyou) }
      </Title>
      <Body data-testid="thank-you-description">
        { translate(vehicle_scan.seriesComplete_report_signing_text) }
        <RingLoaderWrapper>
          <RingLoader
            fontSize="0.5rem"
            color="white"
            bgColor={styles.colorPrimaryButtonBackground}
          />
        </RingLoaderWrapper>
      </Body>
    </>
  );
};

export interface FinalPageProps {
  customerName: string,
  repairDecision?: string,
}

export const FinalPage = (props: FinalPageProps) => {
  const { customerName, repairDecision } = props;
  const { translate } = useTranslate();

  useEffect(() => {
    const timer = setInterval(() => {
      // SPECIAL CASE: When SmartScan is rendered inside an iframe, post message to parent window instead of redirecting
      if (repairDecision && window.parent) {
        window.parent.postMessage({ action: 'inspectionCompleted', windshieldRepairDecision: repairDecision }, '*');
        return null;
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [repairDecision]);

  return (
    <>
      <Title data-testid="thank-you-title">
        { translate(user_feedback.title_thankyou) }
      </Title>
      <Body data-testid="thank-you-description">
        <div className="pr-5 pl-5">
          { translate(vehicle_scan.seriesComplete_publicUser_text, { publicUserPhotoSeriesRecipient: customerName }) }
        </div>
      </Body>
    </>
  );
};

interface Params {
  photoSeriesId: string;
}

export const getRepairDecisionString = (decision: WindshieldRepairReplaceDecision | undefined): string | undefined => {
  switch (decision) {
    case WindshieldRepairReplaceDecision.Repair:
      return 'repairable';
    case WindshieldRepairReplaceDecision.Replace:
      return 'replace';
    case WindshieldRepairReplaceDecision.Uncertain:
      return 'need-review';
    case WindshieldRepairReplaceDecision.NoActionRequired:
      return 'no-action-required';
    default:
      return undefined;
  }
};

const FeedbackThankYou = () => {
  const currentUser = useCurrentUser()!;
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);

  const isSmartScanImageDownloadEnabled = photoSeries.smartScanImageDownloadEnabled === true;

  // We show final page if repair decision is calculated and summary is enabled (Downloading images is implemented on that view already)
  // We show final page if smart scan image download is not enabled
  const [showFinalPage, setShowFinalPage] = useState<boolean>(
    !isSmartScanImageDownloadEnabled ||
    (photoSeries.windshieldRepairReplaceDecision !== undefined && photoSeries.smartScanWindshieldRepairSummaryEnabled)
  );

  const { photoSeriesId } = useParams<Params>();
  const { customerName, returnUrl } = currentUser;

  const repairDecision = getRepairDecisionString(photoSeries.windshieldRepairReplaceDecision);

  if (showFinalPage && photoSeries.reportSigningRequired) {
    return <RedirectToSigning photoSeriesId={photoSeries.id} />;
  }

  // When return url is configured
  if (showFinalPage && returnUrl && returnUrl.length > 0) {
    let modifiedReturnUrl = returnUrl;
    if (repairDecision && photoSeries.addWindshieldRepairDecisionToReturnUrl) {
      const decisionParam = `windshield=${repairDecision}`;
      modifiedReturnUrl += modifiedReturnUrl.includes('?') ? `&${decisionParam}` : `?${decisionParam}`;
    }

    return (
      <RedirectToCompany
        customerName={customerName}
        returnUrl={modifiedReturnUrl}
      />
    );
  }

  if (showFinalPage) {
    return <FinalPage customerName={customerName} repairDecision={repairDecision} />;
  }

  return (
    <DownloadImages
      photoSeriesId={photoSeriesId}
      vin={photoSeries.vehicle.vin}
      onDownloadCompleted={() => setShowFinalPage(true)}
      onNoThanksClicked={() => setShowFinalPage(true)}
    />
  );
};

export default FeedbackThankYou;
